import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { AiDisclaimer } from "@/components/ai-documents/ai-disclaimer";
import {
  DocumentGenerator,
  type GeneratorSeed,
} from "@/components/ai-documents/document-generator";
import { LongformGenerator } from "@/components/ai-documents/longform/longform-generator";
import { Link } from "@/core/lib/navigation";
import {
  buildDocumentDefaults,
  type DocumentRecipientSeed,
  type DocumentTypeDef,
  documentTypeDef,
  genericDocumentTypeDef,
  isDocumentTypeId,
} from "@/core/schema/document-schema";
import { employeeFullName } from "@/core/schema/employee-schema";
import {
  companyQuery,
  customToolQuery,
  documentQuery,
  employeeQuery,
  generatorToolQuery,
  profileQuery,
} from "@/lib/loaders";

/**
 * The generator for a single document type, chosen from the catalog.
 *
 * The loader does what the server component did: validate the `$type` param,
 * resolve the company/profile (signer + letterhead defaults), decide between
 * the letter wizard and the long-form build flow, and bounce an
 * already-generated `?doc` to its dedicated view. A `?doc` / `?duplicate` query
 * selects the seed the client wizard loads; `?employee` (from a profile's Quick
 * Action) prefills and links the recipient to that employee.
 */
export const Route = createFileRoute("/_workspace/generate/$type")({
  validateSearch: (search: Record<string, unknown>) => ({
    doc: typeof search.doc === "string" ? search.doc : undefined,
    duplicate: typeof search.duplicate === "string" ? search.duplicate : undefined,
    employee: typeof search.employee === "string" ? search.employee : undefined,
    incident: typeof search.incident === "string" ? search.incident : undefined,
    view: typeof search.view === "string" ? search.view : undefined,
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, params, deps }) => {
    const { queryClient } = context;
    const view = deps.view === "form" ? "form" : undefined;

    // Built-in types come from the static catalog; any other id must resolve to
    // a live admin-created custom tool, otherwise it's a 404.
    let def: DocumentTypeDef;
    if (isDocumentTypeId(params.type)) {
      def = documentTypeDef(params.type);
    } else {
      const custom = await queryClient.ensureQueryData(customToolQuery(params.type));
      if (!custom) throw notFound();
      def = genericDocumentTypeDef(params.type, custom.name, custom.purpose);
    }

    // Re-opening a saved document to view/edit it (no explicit "Edit form")
    // belongs on the dedicated `/document/{id}` view once it has generated
    // output. Keep the wizard here only for drafts with no output and the "Edit
    // form" path, so old `?doc=` links and bookmarks still resolve correctly.
    if (deps.doc && view !== "form") {
      const existing = await queryClient.ensureQueryData(documentQuery(deps.doc)).catch(() => null);
      if (existing?.output) {
        throw redirect({ to: "/document/$id", params: { id: deps.doc } });
      }
    }

    const seed: GeneratorSeed | null = deps.doc
      ? { mode: "doc", id: deps.doc, view }
      : deps.duplicate
        ? { mode: "duplicate", id: deps.duplicate }
        : null;

    const [company, profile] = await Promise.all([
      queryClient.ensureQueryData(companyQuery),
      queryClient.ensureQueryData(profileQuery),
    ]);

    // Long-form tools (e.g. the Employee Handbook) run a multi-section build
    // flow on a fresh start. Editing an already-assembled document
    // (`?doc`/`?duplicate`) still uses the standard editor.
    const generatorTool = seed
      ? null
      : await queryClient.ensureQueryData(generatorToolQuery(params.type));
    const longform = generatorTool?.generationMode === "longform";

    // A `?employee` deep link prefills the recipient and links the document to
    // that profile — but only on a fresh start (a saved seed carries its own).
    const linkedEmployee =
      !seed && deps.employee
        ? await queryClient.ensureQueryData(employeeQuery(deps.employee))
        : null;

    const recipient: DocumentRecipientSeed | null = linkedEmployee
      ? {
          employeeId: linkedEmployee.id,
          name: employeeFullName(linkedEmployee),
          jobTitle: linkedEmployee.jobTitle ?? "",
          department: linkedEmployee.department ?? "",
        }
      : null;

    return {
      def,
      seed,
      longform,
      generatorTool,
      company,
      profile,
      defaults: buildDocumentDefaults({
        documentType: params.type,
        company,
        profile,
        recipient,
      }),
    };
  },
  component: GenerateDocumentPage,
});

function GenerateDocumentPage() {
  const { incident } = Route.useSearch();
  const { def, seed, longform, generatorTool, company, profile, defaults } = Route.useLoaderData();

  if (longform && generatorTool) {
    return (
      <main className="flex flex-1 flex-col p-4 md:p-6">
        <div className="mx-auto w-full max-w-4xl space-y-6">
          <GeneratorHeader name={def.name} blurb={def.blurb} />
          <LongformGenerator tool={generatorTool} company={company} />
          <AiDisclaimer />
        </div>
      </main>
    );
  }

  return (
    <main className="flex flex-1 flex-col p-4 md:p-6">
      <div className="mx-auto w-full max-w-4xl space-y-6">
        <GeneratorHeader name={def.name} blurb={def.blurb} />
        <DocumentGenerator
          defaults={defaults}
          seed={seed}
          savedSignature={profile?.signature ?? ""}
          company={company}
          typeName={def.name}
          incidentId={!seed ? incident : undefined}
        />
        <AiDisclaimer />
      </div>
    </main>
  );
}

function GeneratorHeader({ name, blurb }: { name: string; blurb: string }) {
  return (
    <div className="space-y-3">
      <Link
        href="/generate"
        className="inline-flex items-center gap-1.5 text-muted-foreground text-sm underline-offset-4 hover:underline"
      >
        <ArrowLeftIcon className="size-4" />
        All document types
      </Link>
      <div className="space-y-1">
        <h1 className="font-bold text-2xl tracking-tight">{name}</h1>
        <p className="text-muted-foreground text-sm">{blurb}</p>
      </div>
    </div>
  );
}
